import { useState, type CSSProperties, type FormEvent } from 'react'
import { login, type AuthUser } from '../api/auth'
import { TENANT } from '../tenant'
import { GOOGLE_SSO_ENABLED } from '../flags'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

// The gate. No part of the dashboard mounts until /api/me confirms a session, so
// this is the only thing an unauthenticated visitor can reach.
export function Login({ onAuthed }: { onAuthed: (u: AuthUser) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Staff SSO is hidden from the public gate — it appears only when a Solvigo staffer
  // navigates to /admin (e.g. /admin/login). Customers and suppliers never see it.
  const staffLogin =
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/\/+$/, '').toLowerCase().startsWith('/admin')

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const user = await login(username.trim(), password)
      onAuthed(user)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.')
      setBusy(false)
    }
  }

  return (
    <div style={S.ground}>
      <div style={S.stack}>
        {/* White logo on the teal backdrop (it'd be invisible inside the white card). */}
        <img src="/minprilla-logo.png" alt={TENANT.productName} style={S.brandLogo} />
        <form style={S.card} onSubmit={submit}>
          <label style={S.label} htmlFor="login-user">Username</label>
        <input
          id="login-user"
          style={S.input}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoFocus
          required
        />

        <label style={S.label} htmlFor="login-pass">Password</label>
        <input
          id="login-pass"
          style={S.input}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        {error && <p style={S.error} role="alert">{error}</p>}

        <button style={{ ...S.button, ...(busy ? S.buttonBusy : {}) }} type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {GOOGLE_SSO_ENABLED && staffLogin && (
          <>
            <div style={S.divider}><span style={S.dividerText}>or</span></div>
            {/* Top-level navigation into the backend OAuth flow (not an XHR). Staff only. */}
            <a style={S.googleButton} href={`${API_BASE}/api/admin/login`}>
              Sign in with Google (Solvigo staff)
            </a>
          </>
        )}

          <p style={S.foot}>Authorised access only. Contact your administrator if you have trouble.</p>
        </form>
        <a href="https://solvigo.ai" target="_blank" rel="noopener noreferrer" style={S.powered}>
          <span>Powered by</span>
          <img src="/solvigo-white.svg" alt="Solvigo" style={S.poweredLogo} />
        </a>
      </div>
    </div>
  )
}

const S: Record<string, CSSProperties> = {
  ground: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--brand-dark)',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    background: 'var(--surface, #fff)',
    borderRadius: 16,
    padding: '36px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    boxShadow: '0 24px 60px rgba(0,0,0,0.32)',
  },
  stack: { width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 },
  brandLogo: { height: 42, width: 'auto', display: 'block' },
  powered: { display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'rgba(255,255,255,0.72)', textDecoration: 'none' },
  poweredLogo: { height: 15, width: 'auto', display: 'block', opacity: 0.92 },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--muted, #4a4a4a)', marginTop: 6 },
  input: {
    height: 42,
    padding: '0 12px',
    borderRadius: 9,
    border: '1px solid var(--line, #e1e1de)',
    fontSize: 15,
    outlineColor: 'var(--accent)',
  },
  error: { margin: '4px 0 0', fontSize: 13, color: '#c0392b', fontWeight: 500 },
  button: {
    marginTop: 16,
    height: 44,
    borderRadius: 10,
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
  buttonBusy: { opacity: 0.7, cursor: 'default' },
  foot: { margin: '18px 0 0', fontSize: 11.5, color: 'var(--muted, #8a8a8a)', textAlign: 'center', lineHeight: 1.5 },
  divider: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '14px 0 2px', borderTop: '1px solid var(--line, #e1e1de)' },
  dividerText: { transform: 'translateY(-9px)', background: 'var(--surface, #fff)', padding: '0 10px', fontSize: 12, color: 'var(--muted, #8a8a8a)' },
  googleButton: {
    height: 44,
    borderRadius: 10,
    border: '1px solid var(--line, #dadce0)',
    background: '#fff',
    color: '#3c4043',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
  },
}
